import * as Y from 'yjs';

import { BaseError } from '@canvas/error';

/**
 * Why an update was rejected.
 *
 * `unsafe-client-id` exists because Yjs generates 32-bit client ids, but its wire
 * format can carry larger values. lib0 decodes them lossily above
 * `Number.MAX_SAFE_INTEGER` and throws `Integer out of Range` on some code paths
 * (the V2 encoding Yjs uses internally for pending structs). One such update can
 * therefore poison a document so that every later apply and every encode throws.
 */
export type InvalidYjsUpdateDetail =
  | { reason: 'undecodable'; cause: unknown }
  | { reason: 'unsafe-client-id'; unsafeClientIds: readonly number[] }
  | { reason: 'unappliable'; cause: unknown };

export type InvalidYjsUpdateReason = InvalidYjsUpdateDetail['reason'];

export type UpdateValidationResult =
  | { valid: true }
  | { valid: false; detail: Exclude<InvalidYjsUpdateDetail, { reason: 'unappliable' }> };

export type UpdateValidationOptions = {
  // Remove this when cleaning up cns-35928_-_reject_unsafe_ids_in_yjs_steps
  rejectUnsafeClientIds?: boolean;
};

export type InvalidYjsUpdateMeta =
  | { reason: 'undecodable' | 'unappliable'; cause: string }
  | { reason: 'unsafe-client-id'; unsafeClientIds: readonly number[] };

const describeCause = (cause: unknown) =>
  cause instanceof Error ? cause.message : String(cause);

/**
 * Plain-data summary of a rejection, safe to log or persist: the raw `cause`
 * is reduced to its message.
 */
export const describeInvalidYjsUpdate = (
  detail: InvalidYjsUpdateDetail,
): InvalidYjsUpdateMeta => {
  switch (detail.reason) {
    case 'undecodable':
    case 'unappliable':
      return { reason: detail.reason, cause: describeCause(detail.cause) };
    case 'unsafe-client-id':
      return { reason: detail.reason, unsafeClientIds: detail.unsafeClientIds };
    default: {
      const _: never = detail;
      return _;
    }
  }
};

export class InvalidYjsUpdateError extends BaseError {
  readonly reason: InvalidYjsUpdateReason;

  constructor(
    readonly update: Uint8Array,
    readonly detail: InvalidYjsUpdateDetail,
  ) {
    super(
      { name: 'InvalidYjsUpdateError', meta: describeInvalidYjsUpdate(detail) },
      'YJS update is invalid',
      'cause' in detail ? { cause: detail.cause } : undefined,
    );
    this.reason = detail.reason;
  }
}

const addUnsafeClientId = (unsafeClientIds: Set<number>, id: Y.ID | null) => {
  if (id !== null && !Number.isSafeInteger(id.client)) {
    unsafeClientIds.add(id.client);
  }
};

const collectUnsafeClientIds = (
  { structs, ds }: ReturnType<typeof Y.decodeUpdate>,
  unsafeClientIds: Set<number>,
) => {
  for (const struct of structs) {
    addUnsafeClientId(unsafeClientIds, struct.id);
    if (struct instanceof Y.Item) {
      addUnsafeClientId(unsafeClientIds, struct.origin);
      addUnsafeClientId(unsafeClientIds, struct.rightOrigin);
    }
  }
  for (const client of ds.clients.keys()) {
    if (!Number.isSafeInteger(client)) {
      unsafeClientIds.add(client);
    }
  }
};

/**
 * Checks that an update decodes. Decoding the whole update is slow for
 * large updates; call this once at the boundary where untrusted updates
 * enter, not on every apply of trusted state.
 *
 * @param update a YJS update, Uint8Array
 * @param options see {@link UpdateValidationOptions}
 */
export function validateUpdate(
  update: Uint8Array,
  { rejectUnsafeClientIds = false }: UpdateValidationOptions = {},
): UpdateValidationResult {
  let decoded: ReturnType<typeof Y.decodeUpdate>;
  try {
    decoded = Y.decodeUpdate(update);
  } catch (cause) {
    return { valid: false, detail: { reason: 'undecodable', cause } };
  }

  // Remove this when cleaning up cns-35928_-_reject_unsafe_ids_in_yjs_steps
  if (!rejectUnsafeClientIds) {
    return { valid: true };
  }

  const unsafeClientIds = new Set<number>();
  collectUnsafeClientIds(decoded, unsafeClientIds);
  if (unsafeClientIds.size > 0) {
    return {
      valid: false,
      detail: { reason: 'unsafe-client-id', unsafeClientIds: [...unsafeClientIds] },
    };
  }

  return { valid: true };
}

/**
 * checks if an update is valid
 *
 * @param update a YJS update, Uint8Array
 * @param options see {@link UpdateValidationOptions}
 * @returns boolean, true if the update is valid
 */
export function isValidUpdate(
  update: Uint8Array,
  options?: UpdateValidationOptions,
): boolean {
  return validateUpdate(update, options).valid;
}

/**
 * throws if an update is invalid
 *
 * @param update a YJS update, Uint8Array
 * @param options see {@link UpdateValidationOptions}
 */
export function validUpdateOrDie(
  update: Uint8Array,
  options?: UpdateValidationOptions,
): void {
  const result = validateUpdate(update, options);
  if (!result.valid) {
    throw new InvalidYjsUpdateError(update, result.detail);
  }
}

/**
 * like Y.applyUpdate but throws a more readable error message
 *
 * @param ydoc Y.Doc
 * @param update the update to apply
 * @param transactionOrigin (optional) see Y.applyUpdate()
 */
export function applyUpdate(
  ydoc: Y.Doc,
  update: Uint8Array,
  transactionOrigin?: any,
): void {
  try {
    Y.applyUpdate(ydoc, update, transactionOrigin);
  } catch (cause) {
    throw new InvalidYjsUpdateError(update, { reason: 'unappliable', cause });
  }
}

/**
 * like Y.applyUpdate but safely no-ops if given an invalid (undecodable) `update` Uint8Array
 *
 * @param ydoc Y.Doc
 * @param update the update to apply
 * @param transactionOrigin (optional) see Y.applyUpdate()
 * @param onError (optional) a callback that is called with an error when invalid
 * @param options (optional) see {@link UpdateValidationOptions}
 * @returns boolean, true if the update was applied
 */
export function safeApplyUpdate(
  ydoc: Y.Doc,
  update: Uint8Array,
  transactionOrigin?: any,
  onError?: (e: InvalidYjsUpdateError) => void,
  options?: UpdateValidationOptions,
): boolean {
  try {
    // please ensure that the only errors that can be thrown from this try
    // are instances of InvalidYjsUpdateError
    validUpdateOrDie(update, options);
    applyUpdate(ydoc, update, transactionOrigin);
    return true;
  } catch (err) {
    onError?.(err as InvalidYjsUpdateError);
    return false;
  }
}
